import React, { useState, useEffect } from "react";
import {
    View,
    StyleSheet,
    Dimensions,
    Platform,
    Text
} from "react-native";
import { useTheme } from "@react-navigation/native";
import { Controller } from 'react-hook-form';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import DropDownPicker from "react-native-dropdown-picker";
import { useTranslation } from "react-i18next";

const width = Dimensions.get("window").width;

const CustomControlledDropdownWithSearch = ({
    control,
    name,
    rules = {},
    type = "PRIMARY",
    disabled = false,
    options = [],
    defValue = undefined,
}) => {
    const { colors } = useTheme();
    const styles = makeStyles(colors);
    const { t } = useTranslation();

    // Local state for dropdown open and items
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(options);

    // Sync options if they change
    useEffect(() => {
        setItems(options);
    }, [options]);

    return (
        <View style={[
            styles.container,
            styles[`container_${type}`],
        ]}>
            <Controller
                control={control}
                name={name}
                rules={rules}
                defaultValue={defValue}
                render={({
                    field: { value, onChange },
                    fieldState: { error }
                }) => (
                    <>
                        <DropDownPicker
                            open={open}
                            value={value}
                            items={items}
                            setOpen={setOpen}
                            setValue={(callbackOrValue) => {
                                const newValue = typeof callbackOrValue === 'function' ? callbackOrValue(value) : callbackOrValue;
                                onChange(newValue);
                            }}
                            setItems={setItems}
                            disabled={disabled}
                            searchable={true}
                            searchPlaceholder={t('common:search')}
                            placeholder={t('common:selectItem')}
                            placeholderStyle={styles.placeholderStyle}
                            style={[
                                styles.input,
                                styles[`input_${type}`],
                                disabled && { backgroundColor: colors.background_disabled }
                            ]}
                            dropDownContainerStyle={[
                                { minHeight: 150, zIndex: 1000 },
                                styles.dropDownContainer,
                                styles[`container_${type}`]
                            ]}
                            listMode={Platform.OS === 'web' ? 'SCROLLVIEW' : 'MODAL'}
                            ArrowDownIconComponent={() => (
                                <Ionicons name="chevron-down" style={styles.icon} />
                            )}
                            ArrowUpIconComponent={() => (
                                <Ionicons name="chevron-up" style={styles.icon} />
                            )}
                            TickIconComponent={() => (
                                <Ionicons name="checkmark-sharp" style={styles.checkmark} />
                            )}
                            CloseIconComponent={() => (
                                <Ionicons name="close-sharp" style={styles.close} />
                            )}
                            modalAnimationType="fade"
                            searchablePlaceholderTextColor="#a1a1a1"
                            searchContainerStyle={styles.searchContainerStyle}
                            searchTextInputStyle={styles.searchTextInputStyle}
                            listMessageTextStyle={styles.listMessageTextStyle}
                            translation={{
                                NOTHING_TO_SHOW: t('common:noResultsFound')
                            }}
                            autoScroll={true}
                        />
                        {error && (
                            <Text style={[styles.error, { color: 'red', alignSelf: 'stretch' }]}>
                                {error.message || 'Error'}
                            </Text>
                        )}
                    </>
                )}
            />
        </View>
    );
};

const makeStyles = (colors) => StyleSheet.create({
    container: {
        borderColor: colors.border_color,
        borderRadius: 10,
        borderWidth: 2,
        alignSelf: 'center',
        position: 'relative',
        zIndex: 1000,
    },

    input: {
        borderRadius: 10,
        borderWidth: 0,
        borderColor: colors.border_card,
        backgroundColor: colors.background_dropdown,
        paddingHorizontal: 0,
        fontSize: 16,
        color: colors.text_primary,
        fontFamily: 'Roboto-Medium',
    },

    dropDownContainer: {
        borderRadius: 10,
        borderWidth: 2,
        borderColor: colors.border_card,
        backgroundColor: colors.background_dropdown,
    },
    placeholderStyle: {
        color: '#a1a1a1',
        fontSize: 16,
        fontFamily: 'Roboto-Medium',
    },
    icon: {
        fontSize: 20,
        color: '#a1a1a1',
        paddingRight: 10,
    },
    checkmark: {
        fontSize: 20,
        color: colors.buttons_primary,
    },
    close: {
        fontSize: 30,
        color: '#a1a1a1',
    },
    searchContainerStyle: {
        borderBottomColor: "#e8e8e8",
    },
    searchTextInputStyle: {
        fontSize: 16,
        fontFamily: 'Roboto-Medium',
        color: colors.text_primary,
        borderColor: colors.border_card,
    },
    listMessageTextStyle: {
        fontSize: 16,
        fontFamily: 'Roboto-Medium',
        color: colors.text_primary,
    },
    container_EDIT_EMPLOYEE: {
        backgroundColor: colors.background_dropdown,
        flex: 1,
        paddingHorizontal: 7,
        margin: 5,
    },
    error: {
        marginTop: 5,
        fontSize: 12,
    },
});

export default CustomControlledDropdownWithSearch;